import React, { useState } from 'react';
import { PageHeader } from '../components/PageHeader';
import {
  FONT_FAMILY,
  FONT_CHARSET,
  FONT_WEIGHTS,
  TYPE_CATEGORIES,
  typeStylesByCategory,
  typeStyleCss,
  type TypeCategory,
} from '../../../design-system/tokens/typography';

/**
 * Foundations › Typography — traslado del frame "Typography" de Figma (node 2:719).
 *
 * Secciones: specimen de la fuente (nombre, muestra, charset y los 4 pesos), una
 * tab por categoría (Heading / Body / Label / Helper) y la tabla de estilos de la
 * categoría activa, cada uno renderizado en vivo con su spec y propósito.
 *
 * Datos desde la fuente de verdad `TYPE_STYLES` (`src/design-system/tokens/typography.ts`).
 */
export const DSTypographyPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TypeCategory>(TYPE_CATEGORIES[0]);

  return (
    <div className="px-10 py-12">
      <div className="mx-auto flex w-full max-w-[800px] flex-col gap-14">
        <PageHeader
          title="Typography"
          description={
            'Conjunto de estilos de texto predefinidos para reutilizar en toda la interfaz.\n' +
            'Tener un conjunto reducido de opciones predefinidas ayuda a acelerar la toma de decisiones y mejora la consistencia del producto.\n' +
            'Ejemplo de uso:\n' +
            '{Cuerpo} + {Size: Small/Base/Large} + {Emphasis: Weak 400/Standard 500/Strong 600/Ultra700}'
          }
        />

        {/* Specimen de la fuente */}
        <section className="flex flex-col gap-12 md:flex-row md:items-start md:justify-between">
          {/* Familia + muestra + charset */}
          <div className="flex flex-col gap-4">
            <p className="text-[22px] text-neutral-900">{FONT_FAMILY}</p>
            <p className="text-[56px] font-semibold leading-none text-neutral-900">Aa</p>
            <p className="whitespace-pre-line text-[24px] leading-[34px] text-neutral-900">
              {FONT_CHARSET}
            </p>
          </div>

          {/* Pesos */}
          <div className="flex flex-col gap-4">
            {FONT_WEIGHTS.map((w) => (
              <div key={w.weight} className="flex items-center gap-4">
                <span
                  className="w-[54px] text-[42px] leading-none text-neutral-900"
                  style={{ fontWeight: w.weight }}
                >
                  Aa
                </span>
                <div>
                  <p className="text-[16px] text-neutral-900" style={{ fontWeight: w.weight }}>
                    {w.label}
                  </p>
                  <p className="text-[14px] text-neutral-500">Font weight: {w.weight}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Tabs por categoría + tabla de estilos de la categoría activa */}
        <div className="flex flex-col gap-8">
          <div role="tablist" aria-label="Categorías de tipografía" className="flex gap-6 border-b border-neutral-200">
            {TYPE_CATEGORIES.map((cat) => {
              const active = cat === activeTab;
              return (
                <button
                  key={cat}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveTab(cat)}
                  className={`-mb-px cursor-pointer border-b-2 px-1 pb-3 text-[15px] transition-colors ${
                    active
                      ? 'border-brand-primary font-semibold text-brand-primary'
                      : 'border-transparent font-medium text-neutral-500 hover:text-neutral-900'
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-10">
            {typeStylesByCategory(activeTab).map((style, i) => (
              <div key={`${style.name}-${i}`} className="flex flex-col">
                {/* Spec header (gris) */}
                <div className="flex items-center gap-8 rounded-md bg-[#f7f7f7] px-4 py-3 text-[13px] font-medium text-neutral-500">
                  <div className="min-w-0 flex-1">
                    Font size: {style.fontSize}px | Line height: {style.lineHeight}px | Letter
                    Spacing: {style.letterSpacing}px
                  </div>
                  <div className="min-w-0 flex-1">Propósito</div>
                </div>

                {/* Fila: muestra en vivo + propósito */}
                <div className="flex items-start gap-8 px-4 py-5">
                  <div className="min-w-0 flex-1 text-neutral-900">
                    <p style={typeStyleCss(style)}>{style.name}</p>
                    <p style={typeStyleCss(style)}>({style.weightLabel})</p>
                  </div>
                  <div className="min-w-0 flex-1 text-[15px] text-neutral-600">{style.purpose}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
